import { useCallback, useMemo } from 'react'

import D3WordCloud from 'react-d3-cloud'
import { scaleLinear } from 'd3-scale'

import { Box, BoxProps } from '@mui/material'
import { WordCloudRecord } from './types'
import { maxFontSize, minFontSize, selectedFill, unselectedFill } from 'constants/wordCloud'

type WordCloudProps = {
	width: number
	height: number
	words: WordCloudRecord[]
	selected?: string | null
	setSelected?: (mesh: string | null) => void
} & BoxProps
export const WordCloud = ({
	width, height,
	words,
	selected, setSelected,
	...others
}: WordCloudProps) => {
	const fontScale = useMemo(
		() => {
			if (words.length === 0) return () => 10
			const counts = words.map(word => word.value)
			const minCount = Math.min(...counts)
			const maxCount = Math.max(...counts)
			return scaleLinear().domain([minCount, maxCount]).range([minFontSize, maxFontSize])
		},
		[words]
	)  
	const fill = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(word: any, index: number) => {
			if (!selected) return unselectedFill(index.toString())
			return selectedFill(
				word.text === selected ? 'selected' : 'notSelected'
			)
		},
		[selected]
	)
	const handleWordClick = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(event: any, word: any) => {
			if (setSelected) {
				setSelected(selected === word.text ? null : word.text)
			}
		},
		[selected, setSelected]
	)
	return (
		<Box
			sx={{
				width, height,
				cursor: 'default',
				"* text": {
					cursor: 'pointer'
				}
			}}
			role="presentation"
			{...others}
		>
			{(words.length > 0 && width > 0 && height > 0) ? (
				<D3WordCloud
					data={words}
					width={width}
					height={height}
					font="Noto Sans JP, sans-serif"
					fontSize={(word) => fontScale(word.value)}
					fill={fill}
					padding={0.5}
					rotate={0} // 回転なし
					random={() => 0.5}  // 固定
					onWordClick={handleWordClick}
				/>
			) : <></>}
		</Box>
	)
}
